import { BadgeInfo, SubsectionBadgeInfo } from '../models/models';
import { OrganizationStatus, SectionStatus } from '../../core/constants/status.constants';

export type StatusPillVariant = BadgeInfo['variant'] | SubsectionBadgeInfo['variant'];
export type StatusPillTone = StatusPillVariant | 'neutral';

const STATUS_PILL_BASE_CLASS =
  'inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium ring-1 ring-inset whitespace-nowrap';

const STATUS_COUNT_PILL_BASE_CLASS =
  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold tracking-tight ring-1 ring-inset transition-colors';

const STATUS_PILL_VARIANT_CLASS_MAP: Record<StatusPillVariant, string> = {
  info: 'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-900/40 dark:text-sky-300 dark:ring-sky-500/20',
  success:
    'bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-900/40 dark:text-green-300 dark:ring-green-500/20',
  error:
    'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-900/40 dark:text-red-300 dark:ring-red-500/20',
  warning:
    'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-900/40 dark:text-amber-300 dark:ring-amber-500/20',
  neutral:
    'bg-slate-100 text-slate-700 ring-slate-600/20 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-500/20',
};

const STATUS_COUNT_PILL_TONE_CLASS_MAP: Record<StatusPillTone, string> = {
  info: 'bg-blue-500/10 text-blue-600 ring-blue-600/20 dark:bg-blue-500/20 dark:text-blue-400 dark:ring-blue-500/30',
  success:
    'bg-emerald-500/10 text-emerald-600 ring-emerald-600/20 dark:bg-emerald-500/20 dark:text-emerald-400 dark:ring-emerald-500/30',
  error:
    'bg-rose-500/10 text-rose-600 ring-rose-600/20 dark:bg-rose-500/20 dark:text-rose-400 dark:ring-rose-500/30',
  warning:
    'bg-amber-500/10 text-amber-600 ring-amber-600/20 dark:bg-amber-500/20 dark:text-amber-400 dark:ring-amber-500/30',
  neutral:
    'bg-gray-500/10 text-gray-600 ring-gray-600/20 dark:bg-gray-500/20 dark:text-gray-400 dark:ring-gray-500/30',
};

export function getStatusPillClass(
  badge: BadgeInfo | SubsectionBadgeInfo,
  withMargin = false,
): string {
  return `${withMargin ? 'ml-2 ' : ''}${STATUS_PILL_BASE_CLASS} ${STATUS_PILL_VARIANT_CLASS_MAP[badge.variant]}`;
}

export function getStatusPillClassFromTone(tone: StatusPillTone): string {
  if (tone === 'neutral') {
    return `${STATUS_PILL_BASE_CLASS} bg-slate-100 text-slate-700 ring-slate-600/20 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-500/20`;
  }

  return `${STATUS_PILL_BASE_CLASS} ${STATUS_PILL_VARIANT_CLASS_MAP[tone]}`;
}

export function getStatusCountPillClass(tone: StatusPillTone): string {
  return `${STATUS_COUNT_PILL_BASE_CLASS} ${STATUS_COUNT_PILL_TONE_CLASS_MAP[tone]}`;
}

export function getStatusTone(status?: string | null): StatusPillTone {
  switch (status?.trim().toUpperCase()) {
    case SectionStatus.DRAFT:
    case OrganizationStatus.DRAFT:
      return 'info';
    case SectionStatus.IN_REVIEW:
    case SectionStatus.DELETION_REQUESTED:
    case OrganizationStatus.TO_BE_REVIEWED:
    case OrganizationStatus.WAITING_TO_BE_SIGNED:
      return 'warning';
    case SectionStatus.ACCEPTED:
    case OrganizationStatus.COMPLETED:
      return 'success';
    case SectionStatus.REJECTED:
    case SectionStatus.DELETION_REJECTED:
      return 'error';
    default:
      return 'neutral';
  }
}

export function isPendingReviewStatus(status?: string | null): boolean {
  switch (status?.trim().toUpperCase()) {
    case SectionStatus.IN_REVIEW:
    case SectionStatus.DELETION_REQUESTED:
    case OrganizationStatus.TO_BE_REVIEWED:
      return true;
    default:
      return false;
  }
}

export function getStatusBadgeInfo(
  status: string | null | undefined,
  resolveLabel: (status?: string | null) => string,
): BadgeInfo | undefined {
  const label = resolveLabel(status);
  const tone = getStatusTone(status);

  if (!label || tone === 'neutral') {
    return undefined;
  }

  return { text: label, variant: tone };
}

export function getStatusTooltipPillClass(status?: string | null): string {
  const tone = getStatusTone(status);

  if (tone === 'neutral') {
    return `subsection-tooltip-status ${STATUS_COUNT_PILL_BASE_CLASS} ${STATUS_COUNT_PILL_TONE_CLASS_MAP[tone]}`;
  }

  return `subsection-tooltip-status ${STATUS_PILL_BASE_CLASS} ${STATUS_PILL_VARIANT_CLASS_MAP[tone]}`;
}
